/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction, getCatalogId, getFriendlyName,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { generateCacheKey, getCategoryUrl } from '@msdyn365-commerce-modules/retail-actions';
import { Category } from '@msdyn365-commerce/retail-proxy';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';

import type { ICategoryMap } from '@msdyn365-commerce-modules/retail-actions/dist/types/get-categories-hierarchy';
import getCategoriesFixedCacheDataAction, { GetCategoriesFixedCacheInput } from './get-categories';

export class CategoriesFixedCacheInput implements IActionInput {
    public readonly maxItems: number;

    public readonly channelId: number;

    public includeRootCategory?: boolean;

    private readonly catalogId: number;

    private readonly sitePath: string;

    private readonly _mappedToHierarchy: boolean;

    private readonly apiSettings: ICommerceApiSettings;

    private readonly locale?: string;

    private readonly _requestContext: IRequestContext;

    public constructor(context: IRequestContext, mappedToHierarchy: boolean, maxItems?: number, includeRootCategory?: boolean) {
        this._requestContext = context;
        this._mappedToHierarchy = mappedToHierarchy;
        this.includeRootCategory = includeRootCategory;
        this.maxItems = maxItems || 250;
        this.channelId = context && context.apiSettings && context.apiSettings.channelId ? +context.apiSettings.channelId : 0;
        this.sitePath = (context && context.sitePath) || '';
        this.apiSettings = context.apiSettings;
        this.locale = context.locale || '';
        this.catalogId = getCatalogId(context);
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'CustomerId', value: this._requestContext.user.isAuthenticated === false
                ? 'anonymous'
                : this._requestContext.user.isOBORequest
                    ? this._requestContext.cookies.getAccountSelectionCookie() || 'anonymous'
                    : this._requestContext.user.customerAccountNumber || 'anonymous'
            },
            { key: 'SitePath', value: this.sitePath },
            { key: 'IncludeRootCategory', value: this.includeRootCategory },
            { key: 'Top', value: this.maxItems },
            { key: 'CatalogId', value: this.catalogId },
            { key: 'Locale', value: this.locale }
        );

    public getCacheObjectType = () => (this._mappedToHierarchy ? 'CategoryHierarchyFixedCache' : 'CategoryFixedCache');

    public dataCacheType = (): CacheType => 'request';

    public getLocale = (): string => this.locale || '';
}

/**
 * Creates a hierarchy of categories based on the ParentCategory property.
 * @param categoryList Categories that will be converted into a hierarchy.
 * @param ctx
 * @param locale
 * @returns Hierarchy of categories in array.
 */
export const mapCategoryToHierarchy = (
    categoryList: Category[],
    ctx: IActionContext,
    locale?: string,
    includeRootCategory?: boolean
): CategoryHierarchy[] => {
    if (!categoryList || categoryList.length === 0) {
        return [];
    }

    const categoryMap: ICategoryMap = {};

    for (const category of categoryList) {
        const localName = getFriendlyName(locale, category.NameTranslations);
        const categoryHierarchy = <CategoryHierarchy>{ ...category };
        categoryHierarchy.NeutralizedName = category.Name;
        categoryHierarchy.Name = localName || categoryHierarchy.NeutralizedName;
        categoryMap[category.RecordId] = categoryHierarchy;
    }

    let rootCategory: CategoryHierarchy | undefined;

    for (const category of Object.values(categoryMap)) {
        const parentId = category.ParentCategory;
        category.Url = getCategoryUrl(category, ctx, categoryMap);
        if (parentId === 0) {
            rootCategory = category;
            continue;
        }

        const parent = parentId && categoryMap[parentId];
        if (parent) {
            parent.Children = parent.Children || [];
            parent.Children.push(category);
        }
    }

    if (!rootCategory) {
        return [];
    }

    if (includeRootCategory) {
        const categoryHierarchy: CategoryHierarchy[] = [];
        categoryHierarchy.push(rootCategory);
        return categoryHierarchy;
    }

    return rootCategory.Children ?? [];
};

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createCategoriesHierarchyInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const topItems = inputData.config && inputData.config.topCategories && Number.parseInt(inputData.config.topCategories, 10);
    return new CategoriesFixedCacheInput(inputData.requestContext, true, topItems);
};

export async function getCategoriesHierarchy(input: CategoriesFixedCacheInput, context: IActionContext): Promise<CategoryHierarchy[]> {
    const categories = await getCategoriesFixedCacheDataAction(new GetCategoriesFixedCacheInput(context.requestContext, false, input.maxItems), context);
    return mapCategoryToHierarchy(categories, context, input.getLocale(), input.includeRootCategory);
}

export const getCategoriesHierarchyFixedCacheDataAction = createObservableDataAction({
    id: 'dlvry/get-categories-hierarchy',
    action: <IAction<CategoryHierarchy[]>>getCategoriesHierarchy,
    input: createCategoriesHierarchyInput
});

export default getCategoriesHierarchyFixedCacheDataAction;
